import React, { createContext, PropsWithChildren, useContext } from "react";
import { enableStaticRendering } from "mobx-react";
import { configure, runInAction } from "mobx";
import _merge from "lodash/merge";
import _isObject from "lodash/isObject";

import _omit from "lodash/omit";
import { API } from "services/ApiService";
import User from "store/user";
import Notifications from "store/notifications";
import Cart from "store/cart";
import Categories from "store/categories";
import Favorites from "store/favorites";
import Location from "store/location";
import Settings from "store/settings";
import Meta from "store/meta";
import Header from "store/header";
import Search from "store/search";
import { ExtendedNextPageContext } from "types/app";
import { Store } from "types/store";

configure({
  useProxies: "never", // need for IE, disable Proxy objects :(
});

const isServer = typeof window === "undefined";
enableStaticRendering(isServer);
let store;

function createNewStore(ctx): Store {
  const s = {
    user: new User(ctx.api),
    notifications: new Notifications(),
    cart: new Cart(),
    categories: new Categories(),
    favorites: new Favorites(),
    location: new Location(),
    settings: new Settings(),
    /** @deprecated */
    meta: new Meta(),
    header: new Header(),
    search: new Search(),

    toProps() {
      const safeToJson = {};
      const storeToProps = _omit(this, [
        "toProps",
        "cart",
        "favorites",
        "settings",
        "meta",
        "header",
        "_hydrated",
      ]);
      Object.keys(storeToProps).forEach(key => {
        safeToJson[key] = this[key].toJSON
          ? this[key].toJSON()
          : _omit(this[key], "ctx");
      });
      return safeToJson;
    },
    _hydrated: false,
  };

  Object.keys(_omit(s, "toProps", "_hydrated")).forEach(key => {
    Object.defineProperty(s[key], "ctx", {
      enumerable: false,
      get() {
        return ctx;
      },
    });
  });

  ctx.store = s;

  return s;
}

export function createStore(ctx: ExtendedNextPageContext): Store {
  if (isServer) return createNewStore(ctx);

  if (!store) {
    store = createNewStore(ctx);
  }
  return store;
}

export const StoreContext = createContext(undefined);

export function useStore(): Store {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within StoreProvider");
  }

  return context;
}

export function StoreProvider({
  children,
  store,
}: PropsWithChildren<{ store: Store }>): ReturnType<
  typeof React.createElement
> {
  return React.createElement(
    StoreContext.Provider,
    {
      value: store,
    },
    children
  );
}

export function wireStoreToContext(ctx: ExtendedNextPageContext): void {
  ctx.store = createStore(ctx);
}

/* todo: move to kind of middleware */
export async function preflightRequest(
  ctx: ExtendedNextPageContext
): Promise<void> {
  wireStoreToContext(ctx);

  /* Server only */
  if (!isServer) return;
  /* todo: use a jwt token cookie for check in future */

  await ctx.store.user.serverAuth(ctx);
  await ctx.store.location.initSeverRegionValue(ctx);
}

/* todo: move to kind of middleware */
export async function loadInitialStoreData(ctx: ExtendedNextPageContext) {
  /* Server only */
  if (!isServer) return;

  await ctx.store.categories.fetch();
}

export function hydrateStore(data: Partial<Store>, api: API): Store {
  //if (isServer) return data as Store;

  const store = createStore({ api } as ExtendedNextPageContext);

  if (store._hydrated) return store;

  data &&
    Object.keys(data).forEach(key => {
      runInAction(() => {
        setInitialDataSafe(store[key], data[key]);
      });
    });
  store._hydrated = true;
  return store;
}

function setInitialDataSafe(storeInstance, data = {}) {
  if (storeInstance.hydrate) {
    storeInstance.hydrate(data);
    return;
  }

  /* todo: make hydration more safe for different types like Set or Map */
  Object.keys(data).forEach(key => {
    if (data[key] && storeInstance[key] !== data[key]) {
      if (_isObject(storeInstance[key])) {
        if (Array.isArray(data[key])) {
          // for LegacyObservableArray objects
          storeInstance[key] = data[key];
        } else {
          storeInstance[key] = _merge(storeInstance[key], data[key]);
        }
      } else {
        storeInstance[key] = data[key];
      }
    }
  });
}
